import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    PersonAdd as PersonAddIcon,
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked,
    Draw as DrawIcon,
    Send as SendIcon,
    Warning as WarningIcon,
    Build as BuildIcon,
    Add as AddIcon,
    Security as SecurityIcon,
    ElectricBolt as ElectricBoltIcon,
    HealthAndSafety as HealthAndSafetyIcon,
    Emergency as EmergencyIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../config/db';
import { LTT_STORE, JOB_STORE, JHA_STORE } from '../../config/constants';
import { createLttSkeleton, ensureLttStructure } from './utils/lttRecordFactory';
import {
    LOTO_CHECKLIST_ITEMS,
    LOTO_CHECKLIST_QUESTIONS,
    PPE_OPTIONS,
    ENERGY_TYPES,
    EMERGENCY_ARRANGEMENTS,
    MODE_JOB,
    MODE_LOCATION
} from './utils/constants';
import { getFullNameEmployeeBy } from '../../services/employeeService';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import SelectFromWeeklyRosterModal from '../jha/SelectFromWeeklyRosterModal';
import SignatureModal from '../jha/SignatureModal';
import axiosInstance from '../../axiosInstance';
import { generateUUID } from '../../utils/uuid';
import useDictation from '../../hooks/useDictation';

function buildLocationOptions(jobs) {
    const seen = new Map();
    jobs.forEach(job => {
        const rawLocation = job.location || job.Location || '';
        if (!rawLocation) return;
        const key = rawLocation.trim().toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, { label: rawLocation, jobCodes: [job.Job || job.jobNumber] });
        } else {
            const entry = seen.get(key);
            const code = job.Job || job.jobNumber;
            if (code && !entry.jobCodes.includes(code)) entry.jobCodes.push(code);
        }
    });
    return Array.from(seen.values());
}

export default function LttForm() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { ltt = null, isNew = false, viewMode = false, fromJha = false, jhaId = null } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lttData, setLttData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Equipment input state
    const [equipmentInput, setEquipmentInput] = useState('');

    // Custom PPE input state
    const [customPpeInput, setCustomPpeInput] = useState('');

    // Modal states
    const [workCarriedOutByModalOpen, setWorkCarriedOutByModalOpen] = useState(false);
    const [workersModalOpen, setWorkersModalOpen] = useState(false);
    const [rosterModalOpen, setRosterModalOpen] = useState(false);
    const [signatureModal, setSignatureModal] = useState({ open: false, type: null, name: '' });
    const [signaturePersonModal, setSignaturePersonModal] = useState({ open: false, type: null });
    const [lockoutPersonModal, setLockoutPersonModal] = useState({ open: false, index: null });
    // Signature clear confirmation dialog
    const [signatureClearDialog, setSignatureClearDialog] = useState({ open: false, pendingData: null, pendingOptions: null });

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const createdRecordIdRef = useRef(ltt?.uuid || null);
    const loadingStartedRef = useRef(false);

    // Voice dictation for Description of Work
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation();

    const isSubmitted = lttData?.status === 'submitted' || lttData?.status === 'completed';
    const isReadOnly = isSubmitted || viewMode;

    const locationOptions = useMemo(() => buildLocationOptions(jobs), [jobs]);

    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job || a.jobNumber) || 0;
            const jobB = parseInt(b.Job || b.jobNumber) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    useEffect(() => {
        loadData();
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, []);

    const loadData = useCallback(async () => {
        // Prevent double execution in React Strict Mode
        if (loadingStartedRef.current) return;
        loadingStartedRef.current = true;

        setLoading(true);
        const db = await initializeDB();

        const allJobs = await db.getAll(JOB_STORE);
        setJobs(allJobs || []);

        // Get logged-in user as default (localStorage first, then IndexedDB fallback)
        const employeeId = localStorage.getItem('employeeId');
        const employeeFullName = localStorage.getItem('employeeFullName') || '';
        let currentEmployee = null;
        if (employeeId) {
            currentEmployee = await getFullNameEmployeeBy(employeeId);
        }
        // Fallback: build from localStorage if IndexedDB didn't have it
        if (!currentEmployee && employeeFullName) {
            currentEmployee = { FullName: employeeFullName, EE: employeeId, employeeId: employeeId };
        }

        if (!db.objectStoreNames.contains(LTT_STORE)) {
            console.error('LTT store not found. Please clear IndexedDB and reload.');
            setSnackbar({ open: true, message: 'Database error. Please clear browser data and reload.', severity: 'error' });
            setLoading(false);
            return;
        }

        const tx = db.transaction(LTT_STORE, 'readwrite');
        const store = tx.objectStore(LTT_STORE);
        let currentRecord = null;

        if (createdRecordIdRef.current) currentRecord = await store.get(createdRecordIdRef.current);
        if (!currentRecord && ltt?.uuid) currentRecord = await store.get(ltt.uuid);
        if (!currentRecord && isNew) {
            const now = dayjs();
            currentRecord = createLttSkeleton({
                header: {
                    workCarriedOutBy: currentEmployee?.FullName || '',
                    workCarriedOutById: currentEmployee?.EE || currentEmployee?.employeeId || null,
                    workCarriedOutByType: 'employee'
                },
                permitDuration: {
                    startDateTime: now.toISOString(),
                    endDateTime: now.add(8, 'hour').toISOString()
                },
                signatures: {
                    checklistInitiator: {
                        name: currentEmployee?.FullName || '',
                        signedById: currentEmployee?.EE || currentEmployee?.employeeId || null,
                        signedByType: 'employee',
                        date: new Date().toISOString(),
                        signature: null
                    },
                    completionVerification: {
                        name: currentEmployee?.FullName || '',
                        signedById: currentEmployee?.EE || currentEmployee?.employeeId || null,
                        signedByType: 'employee',
                        date: new Date().toISOString(),
                        signature: null,
                        completionStatus: null
                    }
                }
            });
            // Save new record immediately to IndexedDB
            await store.put(currentRecord);
            await tx.done;
            createdRecordIdRef.current = currentRecord.uuid;
        }
        if (!currentRecord && ltt) {
            currentRecord = ensureLttStructure(ltt);
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.uuid;
        }
        if (!currentRecord) { setLoading(false); navigate('/ltt'); return; }

        // Ensure logged-in user is set as default for Work Carried Out By and signatures
        const structured = ensureLttStructure(currentRecord);
        if (!structured.header.workCarriedOutBy && currentEmployee) {
            structured.header.workCarriedOutBy = currentEmployee.FullName || '';
            structured.header.workCarriedOutById = currentEmployee.EE || currentEmployee.employeeId || null;
            structured.header.workCarriedOutByType = 'employee';
        }
        if (!structured.signatures.checklistInitiator.name && currentEmployee) {
            structured.signatures.checklistInitiator.name = currentEmployee.FullName || '';
            structured.signatures.checklistInitiator.signedById = currentEmployee.EE || currentEmployee.employeeId || null;
            structured.signatures.checklistInitiator.date = new Date().toISOString();
        }
        if (!structured.signatures.completionVerification.name && currentEmployee) {
            structured.signatures.completionVerification.name = currentEmployee.FullName || '';
            structured.signatures.completionVerification.signedById = currentEmployee.EE || currentEmployee.employeeId || null;
            structured.signatures.completionVerification.date = new Date().toISOString();
        }
        setLttData(structured);
        setLoading(false);
    }, [isNew, ltt, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) { setSaving(false); return; }
        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;
        const db = await initializeDB();
        const tx = db.transaction(LTT_STORE, 'readwrite');
        await tx.objectStore(LTT_STORE).put(recordToSave);
        await tx.done;
        createdRecordIdRef.current = recordToSave.uuid;
        setSaving(false);
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        if (isReadOnly) { setLttData(record); return; }
        const recordWithTimestamp = { ...record, updatedAt: new Date().toISOString() };
        setLttData(recordWithTimestamp);
        pendingRecordRef.current = recordWithTimestamp;
        setSaving(true);
        if (options.immediate) { flushSave(); return; }
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => flushSave(), 400);
    }, [flushSave, isReadOnly]);

    // Update functions
    const updateHeader = (field, value) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({ ...lttData, header: { ...lttData.header, [field]: value } });
    };

    // Dictation toggle for Description of Work
    const toggleDescriptionDictation = () => {
        if (isListening) {
            stopDictation();
        } else {
            startDictation({
                onResult: (transcript) => {
                    const currentText = lttData?.header?.descriptionOfWork || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    updateHeader('descriptionOfWork', newText);
                },
                onError: (error) => {
                    console.warn('Dictation error:', error);
                }
            });
        }
    };

    // Check if any signatures exist (authorization)
    const hasAnySignature = () => {
        const hasAuthSignature = lttData?.signatures && (
            lttData.signatures.checklistInitiator?.signature ||
            lttData.signatures.completionVerification?.signature
        );

        return hasAuthSignature;
    };

    // Clear all signatures from data
    const clearAllSignatures = (data) => {
        return {
            ...data,
            signatures: {
                checklistInitiator: { ...data.signatures?.checklistInitiator, signature: null, acknowledged: false },
                completionVerification: { ...data.signatures?.completionVerification, signature: null, acknowledged: false, completionStatus: null }
            }
        };
    };

    // Helper to confirm and clear signatures when critical data changes
    const confirmAndSaveWithSignatureClear = (newData, options = {}) => {
        if (hasAnySignature()) {
            // Open confirmation dialog
            setSignatureClearDialog({ open: true, pendingData: newData, pendingOptions: options });
            return false;
        } else {
            scheduleSave(newData, options);
        }
        return true;
    };

    // Handle signature clear dialog confirm
    const handleSignatureClearConfirm = () => {
        const { pendingData, pendingOptions } = signatureClearDialog;
        if (pendingData) {
            scheduleSave(clearAllSignatures(pendingData), pendingOptions || {});
        }
        setSignatureClearDialog({ open: false, pendingData: null, pendingOptions: null });
    };

    // Handle signature clear dialog cancel
    const handleSignatureClearCancel = () => {
        setSignatureClearDialog({ open: false, pendingData: null, pendingOptions: null });
    };

    const updatePermitDuration = (field, value) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({ ...lttData, permitDuration: { ...lttData.permitDuration, [field]: value } });
    };

    const updateLotoAnswer = (questionId, answer) => {
        if (!lttData || isReadOnly) return;
        confirmAndSaveWithSignatureClear({
            ...lttData,
            lotoChecklist: {
                ...lttData.lotoChecklist,
                answers: { ...lttData.lotoChecklist.answers, [questionId]: answer }
            }
        });
    };

    const updateControlMeasures = (field, value) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({ ...lttData, controlMeasures: { ...lttData.controlMeasures, [field]: value } });
    };

    const updatePpeSelection = (ppeId, checked) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({
            ...lttData,
            additionalPpe: {
                ...lttData.additionalPpe,
                selections: { ...lttData.additionalPpe.selections, [ppeId]: checked }
            }
        });
    };

    const updateEmergencyArrangement = (id, checked) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({
            ...lttData,
            emergencyArrangements: {
                ...lttData.emergencyArrangements,
                selections: { ...lttData.emergencyArrangements.selections, [id]: checked }
            }
        });
    };

    const updateEmergencyField = (field, value) => {
        if (!lttData || isReadOnly) return;
        scheduleSave({ ...lttData, emergencyArrangements: { ...lttData.emergencyArrangements, [field]: value } });
    };

    // Handlers
    const handleModeChange = (_, newMode) => {
        if (!newMode || isReadOnly) return;
        scheduleSave({
            ...lttData,
            header: { ...lttData.header, mode: newMode, jobId: null, jobCode: '', jobName: '', location: '' }
        });
    };

    const handleJobSelect = (_, job) => {
        if (isReadOnly) return;
        if (job) {
            scheduleSave({
                ...lttData,
                header: {
                    ...lttData.header,
                    jobId: job.id || job.ID || null,
                    jobCode: job.Job || job.jobNumber || '',
                    jobName: job.JobName || job.jobName || '',
                    location: job.location || job.Location || job.JobName || job.jobName || ''
                }
            });
        } else {
            scheduleSave({
                ...lttData,
                header: { ...lttData.header, jobId: null, jobCode: '', jobName: '', location: '' }
            });
        }
    };

    const handleLocationSelect = (_, loc) => {
        if (isReadOnly) return;
        if (loc) {
            scheduleSave({ ...lttData, header: { ...lttData.header, location: loc.label } });
        } else {
            scheduleSave({ ...lttData, header: { ...lttData.header, location: '' } });
        }
    };

    const handleWorkCarriedOutBySelect = (selected) => {
        if (isReadOnly || !selected || selected.length === 0) return;
        const person = selected[0];
        const newName = person.FullName || '';
        const newId = person.EE || person.ee || person.id || null;
        const newType = person._type || 'employee';
        const updatedData = {
            ...lttData,
            header: {
                ...lttData.header,
                workCarriedOutBy: newName,
                workCarriedOutById: newId,
                workCarriedOutByType: newType
            },
            signatures: {
                ...lttData.signatures,
                checklistInitiator: {
                    name: newName,
                    signedById: newId,
                    signedByType: newType,
                    date: new Date().toISOString(),
                    signature: null,
                    acknowledged: false
                },
                completionVerification: {
                    name: newName,
                    signedById: newId,
                    signedByType: newType,
                    date: new Date().toISOString(),
                    signature: null,
                    acknowledged: false,
                    completionStatus: null
                }
            }
        };
        scheduleSave(updatedData);
        setWorkCarriedOutByModalOpen(false);
    };

    // Equipment handling (manual text input only)
    const handleAddEquipment = () => {
        if (isReadOnly || !equipmentInput.trim()) return;
        const current = lttData.header.equipment || [];
        const trimmed = equipmentInput.trim();
        // Avoid duplicates
        if (!current.includes(trimmed)) {
            updateHeader('equipment', [...current, trimmed]);
        }
        setEquipmentInput('');
    };

    const handleRemoveEquipment = (index) => {
        if (isReadOnly) return;
        const current = lttData.header.equipment || [];
        updateHeader('equipment', current.filter((_, i) => i !== index));
    };

    const handleCategory3MultimeterChange = (checked) => {
        if (isReadOnly) return;
        updateHeader('hasCategory3Multimeter', checked);
    };

    // Custom PPE handling
    const handleAddCustomPpe = () => {
        if (isReadOnly || !customPpeInput.trim()) return;
        const current = lttData.additionalPpe?.customPpe || [];
        const trimmed = customPpeInput.trim();
        if (!current.includes(trimmed)) {
            scheduleSave({
                ...lttData,
                additionalPpe: {
                    ...lttData.additionalPpe,
                    customPpe: [...current, trimmed]
                }
            });
        }
        setCustomPpeInput('');
    };

    const handleRemoveCustomPpe = (index) => {
        if (isReadOnly) return;
        const current = lttData.additionalPpe?.customPpe || [];
        scheduleSave({
            ...lttData,
            additionalPpe: {
                ...lttData.additionalPpe,
                customPpe: current.filter((_, i) => i !== index)
            }
        });
    };

    // Lockout Log handlers (V2 - replaces workers)
    const handleAddWorkers = (selected) => {
        if (isReadOnly || !selected || selected.length === 0) return;
        const now = new Date().toISOString();
        const currentLog = lttData.lockoutLog || [];
        const newEntries = selected.map(p => ({
            lockTagNumber: '',
            name: p.FullName || '',
            phone: p.Phone || p.phone || p.MobilePhone || p.mobilePhone || p.CellPhone || p.cellPhone || '',
            dateApplied: now,
            releasedDate: null,
            releasedSignature: null,
            EE: p.EE || p.ee || p.id || null,
            _type: p._type || 'employee'
        }));
        const existingNames = new Set(currentLog.map(e => e.name));
        const uniqueNew = newEntries.filter(e => !existingNames.has(e.name));
        scheduleSave({ ...lttData, lockoutLog: [...currentLog, ...uniqueNew] });
        setWorkersModalOpen(false);
    };

    const handleAddFromRoster = (selected) => {
        if (isReadOnly || !selected || selected.length === 0) return;
        const now = new Date().toISOString();
        const currentLog = lttData.lockoutLog || [];
        const newEntries = selected.map(p => ({
            lockTagNumber: '',
            name: p.FullName || '',
            phone: p.Phone || p.phone || p.MobilePhone || p.mobilePhone || p.CellPhone || p.cellPhone || '',
            dateApplied: now,
            releasedDate: null,
            releasedSignature: null,
            EE: p.EE || p.ee || p.id || null,
            _type: p._type || 'employee'
        }));
        const existingNames = new Set(currentLog.map(e => e.name));
        const uniqueNew = newEntries.filter(e => !existingNames.has(e.name));
        scheduleSave({ ...lttData, lockoutLog: [...currentLog, ...uniqueNew] });
        setRosterModalOpen(false);
    };

    const handleRemoveLockoutEntry = (index) => {
        if (isReadOnly) return;
        const updated = (lttData.lockoutLog || []).filter((_, i) => i !== index);
        scheduleSave({ ...lttData, lockoutLog: updated });
    };

    const handleLockoutPersonChange = (selected) => {
        if (isReadOnly || !selected || selected.length === 0 || lockoutPersonModal.index === null) return;
        const p = selected[0];
        const idx = lockoutPersonModal.index;
        const updated = [...(lttData.lockoutLog || [])];
        updated[idx] = {
            ...updated[idx],
            name: p.FullName || '',
            EE: p.EE || p.ee || p.id || null,
            _type: p._type || 'employee'
        };
        scheduleSave({ ...lttData, lockoutLog: updated });
        setLockoutPersonModal({ open: false, index: null });
    };

    const updateLockoutEntry = (index, field, value) => {
        if (isReadOnly) return;
        const updated = [...(lttData.lockoutLog || [])];
        updated[index] = { ...updated[index], [field]: value };
        scheduleSave({ ...lttData, lockoutLog: updated });
    };

    // Signature handling
    const handleOpenSignatureModal = (type) => {
        if (isReadOnly) return;
        const name = lttData.signatures[type]?.name || '';
        setSignatureModal({ open: true, type, name });
    };

    const handleSaveSignature = (signatureData) => {
        const { type } = signatureModal;
        if (!type) return;
        const now = new Date().toISOString();

        // Handle lockout release signature (type = 'lockoutRelease_<index>')
        if (type.startsWith('lockoutRelease_')) {
            const idx = parseInt(type.split('_')[1], 10);
            const updated = [...(lttData.lockoutLog || [])];
            updated[idx] = {
                ...updated[idx],
                releasedSignature: signatureData,
                releasedDate: updated[idx].releasedDate || now
            };
            scheduleSave({ ...lttData, lockoutLog: updated }, { immediate: true });
            setSignatureModal({ open: false, type: null, name: '' });
            return;
        }

        const existingDate = lttData.signatures?.[type]?.date;
        scheduleSave({
            ...lttData,
            signatures: {
                ...lttData.signatures,
                [type]: {
                    ...lttData.signatures[type],
                    signature: signatureData,
                    date: existingDate || now
                }
            }
        }, { immediate: true });
        setSignatureModal({ open: false, type: null, name: '' });
    };

    const handleClearSignature = (type) => {
        if (isReadOnly) return;
        scheduleSave({
            ...lttData,
            signatures: {
                ...lttData.signatures,
                [type]: {
                    ...lttData.signatures[type],
                    signature: null,
                    date: null
                }
            }
        });
    };

    // Update signature block data (name, date, acknowledged, completionStatus)
    const handleSignatureUpdate = (type, updates) => {
        if (isReadOnly) return;
        scheduleSave({
            ...lttData,
            signatures: {
                ...lttData.signatures,
                [type]: {
                    ...lttData.signatures[type],
                    ...updates
                }
            }
        }, { immediate: true });
    };

    const handleSignaturePersonSelect = (selected) => {
        const { type } = signaturePersonModal;
        if (isReadOnly || !selected || selected.length === 0 || !type) return;
        const person = selected[0];
        const personData = {
            name: person.FullName || '',
            signedById: person.EE || person.ee || person.id || null,
            signedByType: person._type || 'employee',
            signature: null, // Reset signature when person changes
            acknowledged: false,
            date: new Date().toISOString()
        };

        scheduleSave({
            ...lttData,
            signatures: {
                ...lttData.signatures,
                [type]: {
                    ...lttData.signatures[type],
                    ...personData,
                    ...(type === 'completionVerification' ? { completionStatus: null } : {})
                }
            }
        });
        setSignaturePersonModal({ open: false, type: null });
    };

    // Validation
    const allLotoAnswered = useMemo(() => {
        if (!lttData?.lotoChecklist?.answers) return false;
        return LOTO_CHECKLIST_QUESTIONS.every(q => lttData.lotoChecklist.answers[q.id] !== null);
    }, [lttData?.lotoChecklist?.answers]);

    const answeredCount = useMemo(() => {
        if (!lttData?.lotoChecklist?.answers) return 0;
        return Object.values(lttData.lotoChecklist.answers).filter(v => v !== null).length;
    }, [lttData?.lotoChecklist?.answers]);

    const allSignaturesComplete = useMemo(() => {
        if (!lttData?.signatures) return false;
        const sigs = lttData.signatures;
        return ['checklistInitiator', 'completionVerification'].every(
            type => sigs[type]?.signature && sigs[type]?.name
        );
    }, [lttData?.signatures]);

    // Get list of missing validation items for user feedback
    const getMissingValidationItems = useCallback(() => {
        if (!lttData) return [];
        const missing = [];
        const h = lttData.header;
        const pd = lttData.permitDuration;
        const sigs = lttData.signatures || {};

        if (!h.jobCode && !h.location) missing.push('Job or Location');
        if (!h.workCarriedOutBy) missing.push('Work Carried Out By');
        if (!h.descriptionOfWork) missing.push('Description of Work');
        if (!pd.startDateTime) missing.push('Start Date/Time');
        if (!pd.endDateTime) missing.push('End Date/Time');
        if (!allLotoAnswered) missing.push('Complete LOTO Checklist');
        if (!lttData.lockoutLog?.length) missing.push('At least 1 Lockout Log entry');

        // Check each signature
        if (!sigs.checklistInitiator?.signature || !sigs.checklistInitiator?.name) missing.push('Checklist Initiator signature');
        if (!sigs.completionVerification?.signature || !sigs.completionVerification?.name) missing.push('Completion Verification signature');

        return missing;
    }, [lttData, allLotoAnswered]);

    const canSubmit = useMemo(() => {
        if (!lttData) return false;
        const h = lttData.header;
        const pd = lttData.permitDuration;
        const hasLockoutEntries = lttData.lockoutLog?.length > 0;
        return (
            (h.jobCode || h.location) &&
            h.workCarriedOutBy &&
            h.descriptionOfWork &&
            pd.startDateTime && pd.endDateTime &&
            allLotoAnswered &&
            hasLockoutEntries &&
            allSignaturesComplete
        );
    }, [lttData, allLotoAnswered, allSignaturesComplete]);

    const handleSubmit = async () => {
        if (!canSubmit || isReadOnly) return;
        setSaving(true);
        try {
            const now = new Date().toISOString();
            const updated = {
                ...lttData,
                status: 'submitted',
                meta: {
                    ...lttData.meta,
                    submittedAt: now,
                    submittedBy: localStorage.getItem('employeeId'),
                    submittedByName: localStorage.getItem('employeeName') || ''
                }
            };

            // Try to send to API
            try {
                await axiosInstance.post('/ltt/submit', updated);
                updated.backendSynced = true;
            } catch (apiError) {
                console.warn('API submit failed, saving locally:', apiError);
                updated.backendSynced = false;
            }

            pendingRecordRef.current = updated;
            await flushSave();
            setLttData(updated);

            // If coming from JHA, link LTT to JHA and navigate back
            if (fromJha && jhaId) {
                try {
                    const db = await initializeDB();
                    const jhaRecord = await db.get(JHA_STORE, jhaId);
                    if (jhaRecord) {
                        // Find the Lockout/Tagout document and link this LTT
                        const updatedDocsPermits = (jhaRecord.stepTwo?.docsPermits || []).map(doc => {
                            if (doc.value === 'Lockout/Tagout' || doc.specialType === 'ltt') {
                                return {
                                    ...doc,
                                    required: 'Yes Req',
                                    linkedLttUuid: updated.uuid,
                                    linkedLttData: {
                                        uuid: updated.uuid,
                                        location: updated.header?.location || '',
                                        jobCode: updated.header?.jobCode || '',
                                        jobName: updated.header?.jobName || '',
                                        workCarriedOutBy: updated.header?.workCarriedOutBy || '',
                                        submittedAt: updated.meta?.submittedAt
                                    }
                                };
                            }
                            return doc;
                        });

                        const updatedJha = {
                            ...jhaRecord,
                            stepTwo: {
                                ...jhaRecord.stepTwo,
                                docsPermits: updatedDocsPermits
                            }
                        };

                        await db.put(JHA_STORE, updatedJha);
                        setSnackbar({ open: true, message: 'Permit submitted! Returning to JHA...', severity: 'success' });
                        setTimeout(() => {
                            // Back to JHA Documents step (now step-3 after the step swap)
                            navigate('/jha/step-3', { state: { jha: updatedJha, isNew: false } });
                        }, 1000);
                        return;
                    }
                } catch (jhaError) {
                    console.error('Error linking LTT to JHA:', jhaError);
                }
            }

            setSnackbar({ open: true, message: 'Permit submitted successfully!', severity: 'success' });
            setTimeout(() => navigate('/ltt'), 1500);
        } catch (error) {
            console.error('Submit error:', error);
            setSnackbar({ open: true, message: 'Failed to submit permit', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading || !lttData) {
        return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
    }

    const header = lttData.header;
    const selectedJob = sortedJobs.find(j => (j.Job || j.jobNumber) === header.jobCode) || null;
    const selectedLocation = header.location ? locationOptions.find(opt => opt.label === header.location) || null : null;

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header Bar */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={async () => {
                        if (fromJha && jhaId) {
                            // Navigate back to JHA - fetch fresh JHA data first
                            try {
                                const db = await initializeDB();
                                const jhaRecord = await db.get(JHA_STORE, jhaId);
                                if (jhaRecord) {
                                    navigate('/jha/step-3', { state: { jha: jhaRecord, isNew: false } });
                                } else {
                                    navigate('/jha');
                                }
                            } catch (e) {
                                console.error('Error fetching JHA:', e);
                                navigate('/jha');
                            }
                        } else {
                            navigate('/ltt');
                        }
                    }} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>
                        Lock-Tag-Try Checklist (&lt;600V) {isReadOnly && <Chip label="View Only" size="small" color="info" sx={{ ml: 1 }} />}
                        {fromJha && <Chip label="From JHA" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    {saving && <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>Saving...</Typography>}
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
                {/* Section 1: Job Info & Header */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                        JOB INFORMATION
                    </Typography>
                    <Stack spacing={0.75}>
                        {/* Job/Location Toggle + Select */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <ToggleButtonGroup value={header.mode} exclusive onChange={handleModeChange} size="small" disabled={isReadOnly}
                                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.75rem' } }}>
                                <ToggleButton value={MODE_JOB}><BusinessIcon sx={{ fontSize: 16, mr: 0.5 }} />Job</ToggleButton>
                                <ToggleButton value={MODE_LOCATION}><LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />Location</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ flex: 1 }}>
                                {header.mode === MODE_JOB ? (
                                    <Autocomplete
                                        options={sortedJobs}
                                        value={selectedJob}
                                        onChange={handleJobSelect}
                                        getOptionLabel={(o) => {
                                            const code = o.Job || o.jobNumber || '';
                                            const name = o.JobName || o.jobName || '';
                                            return code && name ? `${code} • ${name}` : (code || name || '');
                                        }}
                                        isOptionEqualToValue={(option, value) => (option.Job || option.jobNumber) === (value.Job || value.jobNumber)}
                                        renderInput={(params) => <TextField {...params} size="small" placeholder="Select job" InputProps={{ ...params.InputProps, sx: { fontSize: '0.75rem' } }} />}
                                        disabled={isReadOnly}
                                        size="small"
                                    />
                                ) : (
                                    <Autocomplete
                                        options={locationOptions}
                                        value={selectedLocation}
                                        onChange={handleLocationSelect}
                                        getOptionLabel={(o) => o.label || ''}
                                        isOptionEqualToValue={(option, value) => option.label === value.label}
                                        renderInput={(params) => <TextField {...params} size="small" placeholder="Search location" InputProps={{ ...params.InputProps, sx: { fontSize: '0.75rem' } }} />}
                                        disabled={isReadOnly}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Stack>

                        {/* Work Carried Out By + Description of Work */}
                        <Stack direction="row" spacing={0.5}>
                            <TextField
                                label="Work Carried Out By"
                                value={header.workCarriedOutBy || ''}
                                size="small"
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': { minHeight: 40 }
                                }}
                                InputProps={{ readOnly: true, sx: { fontSize: '0.75rem', cursor: isReadOnly ? 'default' : 'pointer' } }}
                                InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                                onClick={() => !isReadOnly && setWorkCarriedOutByModalOpen(true)}
                            />
                            <TextField
                                label="Description of Work"
                                value={header.descriptionOfWork || ''}
                                onChange={(e) => updateHeader('descriptionOfWork', e.target.value)}
                                size="small"
                                sx={{
                                    flex: 2,
                                    '& .MuiOutlinedInput-root': { minHeight: 40, alignItems: 'flex-start', py: 0.5 }
                                }}
                                multiline
                                maxRows={6}
                                disabled={isReadOnly}
                                InputProps={{
                                    sx: { fontSize: '0.75rem' },
                                    endAdornment: speechSupported && !isReadOnly && (
                                        <InputAdornment position="end" sx={{ mt: 0.5, mr: -0.5 }}>
                                            <IconButton
                                                onClick={toggleDescriptionDictation}
                                                edge="end"
                                                color={isListening ? 'error' : 'default'}
                                                size="small"
                                            >
                                                {isListening ? <MicIcon /> : <MicOffIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                            />
                        </Stack>

                        {/* Equipment + Permit Duration в одном ряду */}
                        <Stack direction="row" spacing={1}>
                            {/* Equipment to be Used */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                                    EQUIPMENT
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={header.hasCategory3Multimeter || false}
                                            onChange={(e) => handleCategory3MultimeterChange(e.target.checked)}
                                            disabled={isReadOnly}
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="caption">Category III 600V Multimeter</Typography>}
                                />
                                <Stack direction="row" spacing={0.5}>
                                    <TextField
                                        value={equipmentInput}
                                        onChange={(e) => setEquipmentInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEquipment(); } }}
                                        size="small"
                                        placeholder="Add equipment..."
                                        disabled={isReadOnly}
                                        fullWidth
                                        InputProps={{ sx: { fontSize: '0.75rem' } }}
                                    />
                                    <Button variant="outlined" size="small" onClick={handleAddEquipment} disabled={isReadOnly || !equipmentInput.trim()} sx={{ minWidth: 36 }}>
                                        <AddIcon fontSize="small" />
                                    </Button>
                                </Stack>
                                {(() => {
                                    const filteredEquipment = (header.equipment || []).filter(item => {
                                        const label = typeof item === 'string' ? item : (item.label || '');
                                        return !label.toLowerCase().includes('category iii') && !label.toLowerCase().includes('600v multimeter');
                                    });
                                    return filteredEquipment.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {filteredEquipment.map((item, idx) => {
                                                const label = typeof item === 'string' ? item : (item.label || item.id || 'Unknown');
                                                return (
                                                    <Chip key={idx} label={label} size="small" color="primary" variant="outlined"
                                                        onDelete={!isReadOnly ? () => handleRemoveEquipment((header.equipment || []).indexOf(item)) : undefined}
                                                        sx={{ fontSize: '0.7rem', height: 22 }} />
                                                );
                                            })}
                                        </Box>
                                    ) : null;
                                })()}
                                {/* Arc Flash PPE Category + NO LABEL (moved from Control Measures) */}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <TextField
                                        label="Arc Flash PPE Category"
                                        value={lttData.controlMeasures?.arcFlashPpeCategory || ''}
                                        onChange={(e) => updateControlMeasures('arcFlashPpeCategory', e.target.value)}
                                        size="small"
                                        sx={{ width: 200 }}
                                        disabled={isReadOnly}
                                        InputProps={{ sx: { fontSize: '0.75rem' } }}
                                        InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={lttData.controlMeasures?.noLabelCategory4Required || false}
                                                onChange={(e) => updateControlMeasures('noLabelCategory4Required', e.target.checked)}
                                                disabled={isReadOnly}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="caption">NO LABEL, Cat 4 (40 kcal) PPE REQD</Typography>}
                                    />
                                </Stack>
                            </Box>

                            {/* Task Duration (renamed from Permit Duration) */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                                    TASK DURATION
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Stack spacing={0.5}>
                                        <DateTimePicker
                                            label="Start"
                                            value={lttData.permitDuration?.startDateTime ? dayjs(lttData.permitDuration.startDateTime) : null}
                                            onAccept={(newValue) => updatePermitDuration('startDateTime', newValue ? newValue.toISOString() : '')}
                                            disabled={isReadOnly}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: true,
                                                    sx: {
                                                        '& .MuiInputBase-root': { fontSize: '0.75rem' },
                                                        '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                                                    }
                                                },
                                                popper: { sx: { zIndex: 1400 } }
                                            }}
                                        />
                                        <DateTimePicker
                                            label="End"
                                            value={lttData.permitDuration?.endDateTime ? dayjs(lttData.permitDuration.endDateTime) : null}
                                            onAccept={(newValue) => updatePermitDuration('endDateTime', newValue ? newValue.toISOString() : '')}
                                            disabled={isReadOnly}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: true,
                                                    sx: {
                                                        '& .MuiInputBase-root': { fontSize: '0.75rem' },
                                                        '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                                                    }
                                                },
                                                popper: { sx: { zIndex: 1400 } }
                                            }}
                                        />
                                    </Stack>
                                </LocalizationProvider>
                            </Box>
                        </Stack>
                    </Stack>
                </Paper>

                {/* Section 3: Energy Identification */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                        ENERGY IDENTIFICATION
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontStyle: 'italic', fontSize: '0.65rem' }}>
                        List: Mechanical, Gravity, Line of Fire, Electrical, Sound, Temperature, Pressure, Steam/Water, Biological/Chemical
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
                        {ENERGY_TYPES.map((e) => (
                            <Chip
                                key={e.id}
                                label={e.label}
                                onClick={() => !isReadOnly && scheduleSave({ ...lttData, energySelections: { ...(lttData.energySelections || {}), [e.id]: !(lttData.energySelections || {})[e.id] } })}
                                color={(lttData.energySelections || {})[e.id] ? 'warning' : 'default'}
                                variant={(lttData.energySelections || {})[e.id] ? 'filled' : 'outlined'}
                                disabled={isReadOnly}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 26 }}
                            />
                        ))}
                    </Stack>
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            multiline rows={2} fullWidth size="small"
                            placeholder="Additional notes..."
                            value={lttData.hazardNotes || ''}
                            onChange={(e) => scheduleSave({ ...lttData, hazardNotes: e.target.value })}
                            disabled={isReadOnly}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                        />
                        {speechSupported && !isReadOnly && (
                            <IconButton size="small" sx={{ position: 'absolute', right: 4, top: 4 }}
                                onClick={() => {
                                    if (isListening) { stopDictation(); } else {
                                        startDictation({ onResult: (t) => { const c = lttData.hazardNotes || ''; scheduleSave({ ...lttData, hazardNotes: c ? `${c} ${t}` : t }); } });
                                    }
                                }}
                                color={isListening ? 'error' : 'default'}>
                                {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>
                </Paper>


                {/* Control Measures */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                        CONTROL MEASURES
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontStyle: 'italic', fontSize: '0.65rem' }}>
                        Indicate method of energy control
                    </Typography>
                    <Stack spacing={1}>
                        {[
                            { key: 'controlQ1', label: 'How are you controlling the energy above?' },
                            { key: 'controlQ2', label: 'How are you releasing stored energy?' },
                            { key: 'controlQ3', label: 'EX: LOCKING BREAKER, VALVE, AIR-GAPPING, etc.' },
                        ].map(q => (
                            <Box key={q.key} sx={{ position: 'relative' }}>
                                <TextField
                                    multiline rows={2} fullWidth size="small"
                                    label={q.label}
                                    value={(lttData.controlMeasures || {})[q.key] || ''}
                                    onChange={(e) => scheduleSave({ ...lttData, controlMeasures: { ...(lttData.controlMeasures || {}), [q.key]: e.target.value } })}
                                    disabled={isReadOnly}
                                    InputProps={{ sx: { fontSize: '0.8rem' } }}
                                    InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                                />
                                {speechSupported && !isReadOnly && (
                                    <IconButton size="small" sx={{ position: 'absolute', right: 4, top: 4 }}
                                        onClick={() => {
                                            if (isListening) { stopDictation(); } else {
                                                startDictation({ onResult: (t) => { const c = (lttData.controlMeasures || {})[q.key] || ''; scheduleSave({ ...lttData, controlMeasures: { ...(lttData.controlMeasures || {}), [q.key]: c ? `${c} ${t}` : t } }); } });
                                            }
                                        }}
                                        color={isListening ? 'error' : 'default'}>
                                        {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Paper>

                {/* Verify Zero Energy */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f', display: 'block', mb: 0.5 }}>
                        VERIFY ZERO ENERGY
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontStyle: 'italic', fontSize: '0.65rem' }}>
                        Indicate all methods of zero energy verification
                    </Typography>
                    <Stack spacing={1}>
                        {[
                            { key: 'verifyQ1', label: 'How are you verifying zero energy?' },
                            { key: 'verifyQ2', label: 'Is there any stored energy?' },
                        ].map(q => (
                            <Box key={q.key} sx={{ position: 'relative' }}>
                                <TextField
                                    multiline rows={2} fullWidth size="small"
                                    label={q.label}
                                    value={(lttData.verifyZeroEnergy || {})[q.key] || ''}
                                    onChange={(e) => scheduleSave({ ...lttData, verifyZeroEnergy: { ...(lttData.verifyZeroEnergy || {}), [q.key]: e.target.value } })}
                                    disabled={isReadOnly}
                                    InputProps={{ sx: { fontSize: '0.8rem' } }}
                                    InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                                />
                                {speechSupported && !isReadOnly && (
                                    <IconButton size="small" sx={{ position: 'absolute', right: 4, top: 4 }}
                                        onClick={() => {
                                            if (isListening) { stopDictation(); } else {
                                                startDictation({ onResult: (t) => { const c = (lttData.verifyZeroEnergy || {})[q.key] || ''; scheduleSave({ ...lttData, verifyZeroEnergy: { ...(lttData.verifyZeroEnergy || {}), [q.key]: c ? `${c} ${t}` : t } }); } });
                                            }
                                        }}
                                        color={isListening ? 'error' : 'default'}>
                                        {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Paper>

                {/* LOTO Checklist */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f' }}>
                            LOTO CHECKLIST
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                            If "NO", do not proceed
                        </Typography>
                    </Stack>
                    <Grid container spacing={0.5}>
                        {LOTO_CHECKLIST_ITEMS.map((item) => {
                            if (item.isHeader) {
                                return (
                                    <Grid item xs={12} key={item.id}>
                                        <Box sx={{ p: 0.75, backgroundColor: '#fff3e0', border: '1px solid #e0e0e0', borderRadius: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '0.8rem' }}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            }
                            const answer = lttData.lotoChecklist?.answers?.[item.id];
                            return (
                                <Grid item xs={6} key={item.id}>
                                    <Box
                                        sx={{
                                            p: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            backgroundColor: answer === 'no' ? '#ffebee' : (answer === 'yes' ? '#e8f5e9' : '#fafafa'),
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 0.5,
                                            height: '100%'
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}>
                                            {item.text}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={answer}
                                            exclusive
                                            onChange={(_, v) => v && updateLotoAnswer(item.id, v)}
                                            size="small"
                                            disabled={isReadOnly}
                                            sx={{
                                                flexShrink: 0,
                                                '& .MuiToggleButton-root': {
                                                    px: 1, py: 0.5, fontSize: '0.7rem', minHeight: 32, minWidth: 36,
                                                    '&.Mui-selected': { fontWeight: 600 },
                                                    '&:focus': { outline: 'none', boxShadow: 'none' },
                                                    '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="yes" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff' }, '&.Mui-selected:focus': { backgroundColor: '#4caf50' }, '&.Mui-selected.Mui-focusVisible': { backgroundColor: '#4caf50' } }}>Y</ToggleButton>
                                            <ToggleButton value="no" sx={{ '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff' }, '&.Mui-selected:focus': { backgroundColor: '#f44336' }, '&.Mui-selected.Mui-focusVisible': { backgroundColor: '#f44336' } }}>N</ToggleButton>
                                            <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#2196f3', color: '#fff' }, '&.Mui-selected:focus': { backgroundColor: '#2196f3' }, '&.Mui-selected.Mui-focusVisible': { backgroundColor: '#2196f3' } }}>NA</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                    {/* Nearest emergency location field */}
                    <TextField
                        label="Nearest emergency service location and contact"
                        value={lttData.emergencyArrangements?.nearestEmergencyLocation || ''}
                        onChange={(e) => updateEmergencyField('nearestEmergencyLocation', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={isReadOnly}
                        sx={{ mt: 1 }}
                        InputProps={{ sx: { fontSize: '0.75rem' } }}
                        InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                    />
                </Paper>

                {/* Section 7: LOCKOUT LOG (V2) */}
                <Paper sx={{ p: 0.75, mb: 0.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#37474f' }}>
                            LOCKOUT LOG ({lttData.lockoutLog?.length || 0})
                        </Typography>
                        {!isReadOnly && (
                            <>
                                <Button variant="outlined" size="small" startIcon={<GroupIcon />} onClick={() => setRosterModalOpen(true)}
                                    sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minHeight: 32 }}>From Roster</Button>
                                <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => setWorkersModalOpen(true)}
                                    sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minHeight: 32 }}>Add Individual</Button>
                            </>
                        )}
                    </Stack>

                    {(!lttData.lockoutLog || lttData.lockoutLog.length === 0) ? (
                        <Typography variant="caption" color="text.secondary">No lockout log entries yet</Typography>
                    ) : (
                        <Stack spacing={0.5}>
                            {lttData.lockoutLog.map((entry, idx) => {
                                const hasReleasedSig = !!entry.releasedSignature;
                                return (
                                    <Paper
                                        key={idx}
                                        variant="outlined"
                                        sx={{
                                            p: 0.75,
                                            borderLeft: `3px solid ${hasReleasedSig ? '#4caf50' : '#1976d2'}`,
                                            backgroundColor: hasReleasedSig ? '#f1f8e9' : '#fafafa'
                                        }}
                                    >
                                        {/* Single row: Lock/Tag # | Name | Applied | Released | Sign | Delete */}
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <TextField
                                                label="Lock/Tag #"
                                                value={entry.lockTagNumber || ''}
                                                onChange={(e) => updateLockoutEntry(idx, 'lockTagNumber', e.target.value)}
                                                size="small"
                                                disabled={isReadOnly}
                                                sx={{ flex: 1, minWidth: 60 }}
                                                InputProps={{ sx: { fontSize: '0.8rem' } }}
                                                InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
                                            />
                                            <TextField
                                                label="Name"
                                                value={entry.name || ''}
                                                size="small"
                                                disabled={isReadOnly}
                                                onClick={() => !isReadOnly && setLockoutPersonModal({ open: true, index: idx })}
                                                sx={{ flex: 1.2, minWidth: 80 }}
                                                InputProps={{ readOnly: true, sx: { fontSize: '0.8rem', fontWeight: 600, cursor: isReadOnly ? 'default' : 'pointer' } }}
                                                InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
                                            />
                                            <TextField
                                                label="Applied"
                                                type="date"
                                                value={entry.dateApplied ? dayjs(entry.dateApplied).format('YYYY-MM-DD') : ''}
                                                onChange={(e) => updateLockoutEntry(idx, 'dateApplied', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                                size="small"
                                                disabled={isReadOnly}
                                                sx={{ width: 125, flexShrink: 0 }}
                                                InputProps={{ sx: { fontSize: '0.75rem' } }}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: '0.7rem' } }}
                                            />
                                            <TextField
                                                label="Released"
                                                type="date"
                                                value={entry.releasedDate ? dayjs(entry.releasedDate).format('YYYY-MM-DD') : ''}
                                                onChange={(e) => updateLockoutEntry(idx, 'releasedDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                                size="small"
                                                disabled={isReadOnly}
                                                sx={{ width: 125, flexShrink: 0 }}
                                                InputProps={{ sx: { fontSize: '0.75rem' } }}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: '0.7rem' } }}
                                            />
                                            {/* Signature preview or Release button */}
                                            {hasReleasedSig ? (
                                                <Box
                                                    onClick={() => !isReadOnly && setSignatureModal({ open: true, type: `lockoutRelease_${idx}`, name: entry.name })}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 100,
                                                        border: '1px solid #4caf50',
                                                        borderRadius: 0.5,
                                                        px: 1,
                                                        backgroundColor: '#fff',
                                                        cursor: isReadOnly ? 'default' : 'pointer',
                                                        height: 40,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <img src={entry.releasedSignature} alt="" style={{ maxWidth: '100%', maxHeight: 36 }} />
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    size="small"
                                                    onClick={() => {
                                                        if (!entry.releasedDate) {
                                                            updateLockoutEntry(idx, 'releasedDate', new Date().toISOString());
                                                        }
                                                        setSignatureModal({ open: true, type: `lockoutRelease_${idx}`, name: entry.name });
                                                    }}
                                                    disabled={isReadOnly}
                                                    sx={{ flex: 1, minWidth: 100, minHeight: 40, fontSize: '0.75rem' }}
                                                    startIcon={<DrawIcon sx={{ fontSize: 16 }} />}
                                                >
                                                    Release
                                                </Button>
                                            )}
                                            {!isReadOnly && (
                                                <IconButton size="small" onClick={() => handleRemoveLockoutEntry(idx)} sx={{ p: 0.25, flexShrink: 0 }}>
                                                    <DeleteIcon sx={{ fontSize: 16 }} color="error" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>

                {/* Section 9: Signatures */}
                <Paper sx={{ p: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#37474f', mb: 1 }}>
                        AUTHORIZATION & SIGNATURES
                    </Typography>

                    {/* Validation check */}
                    {(() => {
                        const missingFields = [];
                        if (!lttData.header?.jobCode && !lttData.header?.location) missingFields.push('Job or Location');
                        if (!lttData.lockoutLog?.length) missingFields.push('Lockout Log');

                        const canSign = missingFields.length === 0;
                        const disabledMessage = missingFields.length > 0
                            ? `Please fill in: ${missingFields.join(', ')}`
                            : null;

                        return (
                            <>
                                {!canSign && (
                                    <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                                        {disabledMessage}
                                    </Alert>
                                )}
                                <Stack spacing={1.5}>
                                    {/* Checklist Initiator (V2 - was Permit Issuer) */}
                                    <AuthSignatureBlock
                                        title="Checklist Initiator"
                                        description="I confirm that I have verified the above information & associated Risk Assessment for this work & ensured that all necessary precautions have been taken."
                                        data={lttData.signatures?.checklistInitiator}
                                        isReadOnly={isReadOnly}
                                        onUpdate={(updates) => handleSignatureUpdate('checklistInitiator', updates)}
                                        onSign={() => handleOpenSignatureModal('checklistInitiator')}
                                        onSelectPerson={() => setSignaturePersonModal({ open: true, type: 'checklistInitiator' })}
                                        disabled={!canSign}
                                    />

                                    {/* Completion Verification (V2 - was Hand Back) */}
                                    <AuthSignatureBlock
                                        title="Completion Verification"
                                        description="I confirm the work has been completed."
                                        data={lttData.signatures?.completionVerification}
                                        isReadOnly={isReadOnly}
                                        onUpdate={(updates) => handleSignatureUpdate('completionVerification', updates)}
                                        onSign={() => handleOpenSignatureModal('completionVerification')}
                                        onSelectPerson={() => setSignaturePersonModal({ open: true, type: 'completionVerification' })}
                                        showCompletionStatus={true}
                                        completionStatus={lttData.signatures?.completionVerification?.completionStatus}
                                        onCompletionStatusChange={(val) => handleSignatureUpdate('completionVerification', { completionStatus: val })}
                                        color="primary"
                                        disabled={!canSign}
                                    />
                                </Stack>
                            </>
                        );
                    })()}
                </Paper>
            </Box>

            {/* Bottom Navigation */}
            <Box sx={{
                backgroundColor: canSubmit ? '#e8f5e9' : '#fff3e0',
                borderTop: canSubmit ? '1px solid #a5d6a7' : '1px solid #ffcc80',
                px: 1, py: 0.5
            }}>
                <Stack spacing={0.5}>
                    {/* Show missing items when not ready to submit */}
                    {!canSubmit && !isReadOnly && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100' }}>
                                Missing:
                            </Typography>
                            {getMissingValidationItems().map((item, idx) => (
                                <Chip
                                    key={idx}
                                    label={item}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                            ))}
                        </Box>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 500, color: canSubmit ? '#2e7d32' : '#e65100' }}>
                            {canSubmit
                                ? '✓ Ready to submit'
                                : allLotoAnswered
                                    ? `Checklist complete. ${!allSignaturesComplete ? 'Signatures required.' : 'Complete missing items.'}`
                                    : `LOTO Checklist: ${answeredCount}/${LOTO_CHECKLIST_QUESTIONS.length}`
                            }
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {saving && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                            {fromJha && !isReadOnly && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={async () => {
                                        try {
                                            const db = await initializeDB();
                                            const jhaRecord = await db.get(JHA_STORE, jhaId);
                                            if (jhaRecord) {
                                                navigate('/jha/step-3', { state: { jha: jhaRecord, isNew: false } });
                                            } else {
                                                navigate('/jha');
                                            }
                                        } catch (e) {
                                            console.error('Error fetching JHA:', e);
                                            navigate('/jha');
                                        }
                                    }}
                                >
                                    Back to JHA
                                </Button>
                            )}
                            {!isReadOnly && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    endIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                                    onClick={handleSubmit}
                                    disabled={!canSubmit || saving}
                                >
                                    {saving ? 'Submitting...' : (fromJha ? 'Submit & Return to JHA' : 'Submit Checklist')}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </Box>

            {/* Modals */}
            <SelectEmployeesAndTempsModal
                open={workCarriedOutByModalOpen}
                onClose={() => setWorkCarriedOutByModalOpen(false)}
                onSelect={handleWorkCarriedOutBySelect}
                alreadySelected={header.workCarriedOutBy ? [{ FullName: header.workCarriedOutBy, _type: header.workCarriedOutByType || 'employee' }] : []}
                singleSelect={true}
            />

            <SelectEmployeesAndTempsModal
                open={workersModalOpen}
                onClose={() => setWorkersModalOpen(false)}
                onSelect={handleAddWorkers}
                alreadySelected={(lttData.lockoutLog || []).map(e => ({ FullName: e.name, _type: e._type || 'employee' }))}
                singleSelect={false}
            />

            <SelectFromWeeklyRosterModal
                open={rosterModalOpen}
                onClose={() => setRosterModalOpen(false)}
                onSelect={handleAddFromRoster}
                alreadySelected={(lttData.lockoutLog || []).map(e => ({ FullName: e.name, _type: e._type || 'employee' }))}
            />

            <SelectEmployeesAndTempsModal
                open={signaturePersonModal.open}
                onClose={() => setSignaturePersonModal({ open: false, type: null })}
                onSelect={handleSignaturePersonSelect}
                alreadySelected={lttData.signatures?.[signaturePersonModal.type]?.name ? [{ FullName: lttData.signatures[signaturePersonModal.type].name, _type: lttData.signatures[signaturePersonModal.type].signedByType || 'employee' }] : []}
                singleSelect={true}
            />

            <SelectEmployeesAndTempsModal
                open={lockoutPersonModal.open}
                onClose={() => setLockoutPersonModal({ open: false, index: null })}
                onSelect={handleLockoutPersonChange}
                alreadySelected={lockoutPersonModal.index !== null && lttData.lockoutLog?.[lockoutPersonModal.index]?.name ? [{ FullName: lttData.lockoutLog[lockoutPersonModal.index].name, _type: lttData.lockoutLog[lockoutPersonModal.index]._type || 'employee' }] : []}
                singleSelect={true}
            />

            <SignatureModal
                allowAuto
                open={signatureModal.open}
                onClose={() => setSignatureModal({ open: false, type: null, name: '' })}
                existingSignature={
                    signatureModal.type?.startsWith('lockoutRelease_')
                        ? (lttData.lockoutLog?.[parseInt(signatureModal.type.split('_')[1], 10)]?.releasedSignature || '')
                        : (lttData.signatures?.[signatureModal.type]?.signature || '')
                }
                employeeName={signatureModal.name || lttData.signatures?.[signatureModal.type]?.name || ''}
                onSave={handleSaveSignature}
            />

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Signature Clear Confirmation Dialog */}
            <Dialog
                open={signatureClearDialog.open}
                onClose={handleSignatureClearCancel}
            >
                <DialogTitle sx={{ backgroundColor: '#ff9800', color: '#fff', py: 1.5 }}>
                    ⚠️ Warning: Signatures Will Be Cleared
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText>
                        <strong>Signatures have already been added to this permit.</strong>
                        <br /><br />
                        If you make this change, all worker signatures (Sign In/Out) and authorization signatures will be cleared.
                        <br /><br />
                        Everyone will need to re-sign after this change.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleSignatureClearCancel} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSignatureClearConfirm} variant="contained" color="warning" autoFocus>
                        Yes, Clear Signatures & Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// Authorization Signature Block Component
function AuthSignatureBlock({
    title,
    description,
    data,
    isReadOnly,
    onUpdate,
    onSign,
    onSelectPerson, // Prop to open person selection modal
    showCompletionStatus = false,
    completionStatus,
    onCompletionStatusChange,
    color = 'default',
    disabled = false
}) {
    const hasSignature = Boolean(data?.signature);
    const isAcknowledged = Boolean(data?.acknowledged);
    const defaultName = localStorage.getItem('employeeFullName') || localStorage.getItem('employeeName') || '';
    const datePickerRef = React.useRef(null);

    const isDisabled = isReadOnly || disabled;

    // Initialize with default values if empty (prefill logged-in user regardless of disabled state)
    React.useEffect(() => {
        if (!isReadOnly && !data?.name && defaultName) {
            onUpdate({ name: defaultName, date: new Date().toISOString() });
        }
    }, [disabled]);

    const handleNameChange = (e) => {
        if (isDisabled) return;
        onUpdate({ name: e.target.value });
    };

    const handleDateChange = (newValue) => {
        if (isDisabled) return;
        onUpdate({ date: newValue ? newValue.toISOString() : null });
    };

    const handleAcknowledgeChange = (e) => {
        if (isDisabled) return;
        onUpdate({ acknowledged: e.target.checked });
    };

    const handleClearSignature = () => {
        if (isDisabled) return;
        onUpdate({ signature: null, acknowledged: false });
    };

    const borderColor = disabled ? '#e0e0e0' : (hasSignature ? '#4caf50' : (color === 'error' ? '#ffcdd2' : (color === 'primary' ? '#bbdefb' : '#e0e0e0')));
    const bgColor = disabled ? '#f5f5f5' : (hasSignature ? '#e8f5e9' : (color === 'error' ? '#ffebee' : (color === 'primary' ? '#e3f2fd' : '#fafafa')));
    const titleColor = disabled ? '#9e9e9e' : (color === 'error' ? '#c62828' : (color === 'primary' ? '#1565c0' : '#37474f'));

    // For Hand Back - require completion status before allowing sign
    const canSign = showCompletionStatus ? (isAcknowledged && completionStatus) : isAcknowledged;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 1,
                border: `1px solid ${borderColor}`,
                borderLeft: `4px solid ${hasSignature ? '#4caf50' : (color === 'error' ? '#f44336' : (color === 'primary' ? '#1976d2' : '#9e9e9e'))}`,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{ backgroundColor: bgColor, p: 1, borderBottom: '1px solid #e0e0e0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: titleColor }}>{title}</Typography>
                    </Box>
                    <Chip
                        label={hasSignature ? 'Signed' : 'Pending'}
                        color={hasSignature ? 'success' : 'default'}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                    />
                </Stack>
            </Box>

            {/* Content */}
            <Box sx={{ p: 1.5 }}>
                {/* Completion Status for Hand Back */}
                {showCompletionStatus && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Work Status:</Typography>
                        <ToggleButtonGroup
                            value={completionStatus}
                            exclusive
                            onChange={(_, val) => val && onCompletionStatusChange(val)}
                            size="small"
                            disabled={isDisabled}
                            sx={{ '& .MuiToggleButton-root': { px: 2 } }}
                        >
                            <ToggleButton value="completed" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff' } }}>
                                Completed
                            </ToggleButton>
                            <ToggleButton value="partiallyCompleted" sx={{ '&.Mui-selected': { backgroundColor: '#ff9800', color: '#fff' } }}>
                                Partially Completed
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* Name, Date and Signature in one row */}
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                        <TextField
                            label="Name"
                            value={data?.name || ''}
                            onChange={handleNameChange}
                            onClick={() => !isDisabled && onSelectPerson && onSelectPerson()}
                            size="small"
                            disabled={isDisabled}
                            fullWidth
                            InputProps={{
                                readOnly: !!onSelectPerson,
                                sx: onSelectPerson ? { cursor: 'pointer' } : {}
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label="Date"
                                value={data?.date ? dayjs(data.date) : dayjs()}
                                onAccept={handleDateChange}
                                disabled={isDisabled}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true
                                    },
                                    popper: { sx: { zIndex: 1400 } }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {hasSignature ? (
                                <>
                                    <Box
                                        onClick={() => !isDisabled && onSign()}
                                        sx={{
                                            flex: 1,
                                            border: '1px solid #4caf50',
                                            borderRadius: 1,
                                            p: 0.5,
                                            backgroundColor: '#fff',
                                            cursor: isDisabled ? 'default' : 'pointer',
                                            '&:hover': !isDisabled ? { borderColor: '#1976d2', backgroundColor: '#f5f9ff' } : {},
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <img src={data.signature} alt="Sig" style={{ maxWidth: '100%', maxHeight: 36, display: 'block' }} />
                                    </Box>
                                    {!isDisabled && (
                                        <IconButton size="small" onClick={handleClearSignature} color="error" sx={{ p: 0.25 }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </>
                            ) : (
                                <Button
                                    variant="outlined"
                                    onClick={onSign}
                                    disabled={isDisabled || !canSign || !data?.name}
                                    size="small"
                                    fullWidth
                                    sx={{ height: 40, fontSize: '0.7rem' }}
                                >
                                    {!isAcknowledged ? '☐ Check first' : (showCompletionStatus && !completionStatus ? 'Select status' : 'Sign')}
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                {/* Acknowledgment Checkbox with specific description */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAcknowledged}
                            onChange={handleAcknowledgeChange}
                            disabled={isDisabled || hasSignature}
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="caption" color="text.secondary">
                            {description}
                        </Typography>
                    }
                    sx={{ m: 0, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
                />
            </Box>
        </Paper>
    );
}
